import { doesAiAnalysisHasAnyUpdates } from '@aibees/elephant/components/person-ai-analysis-info/utils';
import { PeopleTableInfo } from '@aibees/elephant/feature/search-table-people';
import {
  doesContactDetailHaveAnyUpdates,
  isJobChangeHappened,
} from '@aibees/elephant/feature/search-table-people/components/people-drawer/components/contact-details/utils';
import { doesEmailEnrichmentHaveAnyUpdates } from '@aibees/elephant/feature/search-table-people/components/people-quick-info/utils';
import { ROUTES } from '@aibees/elephant/router';
import { ENRICHMENT_STATE } from '@aibees/elephant/types';
import { useMemo } from 'react';

export type PersonUpdatesData = {
  hasAnyUpdates: boolean;
  hasProfileUpdates: boolean;
  profileUpdatesCount: number;
  hasEmailUpdates: boolean;
  emailUpdatesCount: number;
  hasAiAnalysisUpdates: boolean;
  aiAnalysisUpdatesCount: number;
  totalUpdatesCount: number;
  updatePathRoute: string;
  needsEnrichEmail: boolean;
};

export function getPersonUpdatesData(
  data?: PeopleTableInfo
): PersonUpdatesData {
  const hasProfileUpdates = doesContactDetailHaveAnyUpdates(data);
  // The backend's own counter, replacing changes.CONTACT.length — which was
  // standing in for a count nobody had defined it as. Gated exactly like
  // changes.CONTACT, with EXPERIENCE the documented exception: always counted,
  // locked profile or not. Absent counts as zero.
  const profileUpdatesCount = data?.updates?.profileInsights ?? 0;

  const hasEmailUpdates = doesEmailEnrichmentHaveAnyUpdates(data);
  const needsEnrichEmail = isJobChangeHappened(data);
  // updates.contactDetails is company-change-only by design, so the front-end
  // predicate stays OR-ed on top permanently rather than as a stopgap: nothing
  // on the backend counts a plain changes.EMAIL update or an EXPIRED email.
  // Still capped at 1 — this drives a marker, not a tally.
  //
  // The PROCESSING mask is front-end-only. The backend has no reason to model an
  // in-flight enrichment, so without masking here the bell stays lit all the way
  // through one. doesEmailEnrichmentHaveAnyUpdates already masks its own half;
  // this covers the backend counter too.
  const isEmailProcessing =
    data?.revelation?.email?.state === ENRICHMENT_STATE.PROCESSING;
  const emailUpdatesCount =
    !isEmailProcessing && (!!data?.updates?.contactDetails || hasEmailUpdates)
      ? 1
      : 0;

  const hasAiAnalysisUpdates = doesAiAnalysisHasAnyUpdates(data);
  const aiAnalysisUpdatesCount = hasAiAnalysisUpdates
    ? data?.changes?.ANALYSIS?.length ?? 0
    : 0;

  // Left on the front-end predicates on purpose. It drives five dot badges, and
  // updates.total includes analysis — a person with only a personality change
  // would light all five with nothing behind them.
  const hasAnyUpdates =
    hasProfileUpdates ||
    hasEmailUpdates ||
    hasAiAnalysisUpdates ||
    needsEnrichEmail;

  // Summed rather than read off updates.total, deliberately against the backend
  // doc's advice: total includes analysis, and the UI does not expose Personality
  // Analysis today, so reading it would display a count for a feature with no
  // surface. The two agree while updates.analysis is always 0, and only the sum
  // stays correct on the day Personality is switched back on.
  //
  // A company change raises profileInsights and contactDetails together, so one
  // job change reads as (2). That is intended (F-17, decided 29 Aug 2026): the
  // user gets two separate things to act on — the work experience change on
  // Profile Insights, and the email that belongs to the previous company and is
  // now outdated on Contact Details. Two tabs, two badges, two actions, so (2) is
  // the honest count. Collapsing it to (1) would hide half the work.
  const totalUpdatesCount =
    profileUpdatesCount + emailUpdatesCount + aiAnalysisUpdatesCount;

  // Records where the backend counter and the old derivation disagree will now
  // open on a different tab. Expected churn, not a bug.
  const updatePathRoute =
    profileUpdatesCount > 0
      ? ROUTES.peopleUpdatesProfile
      : emailUpdatesCount > 0
      ? ROUTES.peopleUpdatesContact
      : aiAnalysisUpdatesCount > 0
      ? ROUTES.peopleUpdatesAnalysis
      : ROUTES.peopleUpdatesProfile;

  return {
    hasAnyUpdates,
    hasProfileUpdates,
    profileUpdatesCount,
    hasEmailUpdates,
    emailUpdatesCount,
    hasAiAnalysisUpdates,
    aiAnalysisUpdatesCount,
    totalUpdatesCount,
    updatePathRoute,
    needsEnrichEmail,
  };
}

export function useGetPersonUpdatesData(data?: PeopleTableInfo) {
  return useMemo(() => getPersonUpdatesData(data), [data]);
}
